import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Stat } from '../types';
import { useAuth } from '../hooks/useAuth';
import { statService } from '../services/api/statService';
import { MOCK_STATS } from '../services/data/mockData';

const DEMO_USER_ID = 'u1';

interface DataContextType {
  stats: Stat[];
  loading: boolean;
  error: Error | null;
  refetchStats: () => Promise<void>;
  addSessionStat: (stat: Stat) => void;
  removeSessionStat: (id: string) => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile } = useAuth();
  const [stats, setStats] = useState<Stat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const sessionStatsRef = useRef<Stat[]>([...MOCK_STATS]);

  const userId = profile?.id || null;
  const isDemo = userId === DEMO_USER_ID;

  const fetchStats = useCallback(async () => {
    if (!userId) {
      setStats([]);
      return;
    }
    if (isDemo) {
      setStats([...sessionStatsRef.current]);
      return;
    }
    setLoading(true);
    try {
      const data = await statService.getAllByUserId(userId);
      setStats(data);
    } catch (e: any) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [userId, isDemo]);

  useEffect(() => {
    if (isDemo) sessionStatsRef.current = [...MOCK_STATS];
    fetchStats();
  }, [fetchStats, isDemo]);

  const addSessionStat = useCallback(
    (stat: Stat) => {
      if (isDemo) {
        sessionStatsRef.current = [stat, ...sessionStatsRef.current];
        setStats([...sessionStatsRef.current]);
      } else {
        fetchStats();
      }
    },
    [isDemo, fetchStats]
  );

  const removeSessionStat = useCallback(
    (id: string) => {
      if (isDemo) {
        sessionStatsRef.current = sessionStatsRef.current.filter((s) => s.id !== id);
        setStats([...sessionStatsRef.current]);
      } else {
        fetchStats();
      }
    },
    [isDemo, fetchStats]
  );

  return (
    <DataContext.Provider
      value={{ stats, loading, error, refetchStats: fetchStats, addSessionStat, removeSessionStat }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useDataContext = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error('useDataContext must be used within DataProvider');
  return context;
};
